import { ref } from 'vue'

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'nl'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

const STORAGE_KEY = 'dt-language'

/** Gets the browser's preferred 2-letter language code, falls back to 'en' */
export const getBrowserLanguage = (): SupportedLanguage => {
  const lang = navigator.language ?? navigator.languages?.[0] ?? 'en'
  const code = lang.split('-')[0].toLowerCase()
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(code)
    ? (code as SupportedLanguage)
    : 'en'
}

/** Gets the persisted user language preference from localStorage */
export const getStoredLanguage = (): SupportedLanguage | null => {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored && (SUPPORTED_LANGUAGES as readonly string[]).includes(stored)
    ? (stored as SupportedLanguage)
    : null
}

/** Persists the user language preference to localStorage */
export const storeLanguage = (lang: SupportedLanguage): void => {
  localStorage.setItem(STORAGE_KEY, lang)
}

/**
 * Reactive ref holding the current application language.
 * This is updated by the language boot file and useLanguage composable.
 * It is intentionally module-level so that utility functions (e.g., dtdl.ts)
 * can read it without needing Vue context.
 */
export const currentLanguage = ref<SupportedLanguage>(getStoredLanguage() ?? getBrowserLanguage())
